import React, { useState } from "react";
import {
  FaChevronLeft,
  FaChevronRight,
  FaPlus,
  FaRegEdit
} from "react-icons/fa";
import {
  format,
  isToday,
  parseISO,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
} from "date-fns";

export default function Holidays() {
  const [holidays, setHolidays] = useState([
    { date: "2025-01-01", name: "New Year's Day" },
    { date: "2025-01-26", name: "Republic Day" },
    { date: "2025-02-26", name: "Shivratri" },
    { date: "2025-03-14", name: "Holi" },
    { date: "2025-03-15", name: "Bhai Dooj" },
    { date: "2025-08-09", name: "Raksha Bandhan" },
    { date: "2025-08-15", name: "Independence Day" },
    { date: "2025-08-16", name: "Janmashtmi" },
    { date: "2025-10-02", name: "Gandhi Jayanti & Dusshera" },
    { date: "2025-10-20", name: "Diwali" },
    { date: "2025-10-22", name: "Goverdhan Puja" },
    { date: "2025-10-23", name: "Bhai Dooj" },
  ]);

  const [modalOpen, setModalOpen] = useState(false);
  const [currentHoliday, setCurrentHoliday] = useState({ name: "", date: "" });
  const [editIndex, setEditIndex] = useState(null);

  const [monthDate, setMonthDate] = useState(new Date());

  // Build calendar days
  const days = eachDayOfInterval({
    start: startOfMonth(monthDate),
    end: endOfMonth(monthDate),
  });

  const holidaysByDate = holidays.reduce((acc, h) => {
    acc[h.date] = h.name;
    return acc;
  }, {});

  // Open Modal
  const openModal = (holiday = { name: "", date: "" }, index = null) => {
    setCurrentHoliday(holiday);
    setEditIndex(index);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setCurrentHoliday({ name: "", date: "" });
    setEditIndex(null);
  };

  const saveHoliday = () => {
    if (editIndex !== null) {
      const updated = [...holidays];
      updated[editIndex] = currentHoliday;
      setHolidays(updated);
    } else {
      setHolidays([...holidays, currentHoliday]);
    }
    closeModal();
  };

  return (
    <div className="ml-72 mt-16 p-6 bg-gray-100 min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold bg-gradient-to-r from-blue-700 to-indigo-500 bg-clip-text text-transparent">
          📅 Holiday Calendar 2025
        </h2>

        <button
          onClick={() => openModal()}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700"
        >
          <FaPlus size={14} /> Add Holiday
        </button>
      </div>

      {/* Month Navigation */}
      <div className="flex justify-between items-center bg-white p-4 border rounded-lg shadow mb-4">
        <button
          onClick={() =>
            setMonthDate(new Date(monthDate.setMonth(monthDate.getMonth() - 1)))
          }
          className="text-gray-600 hover:text-gray-900"
        >
          <FaChevronLeft />
        </button>

        <h3 className="text-lg font-bold">{format(monthDate, "MMMM yyyy")}</h3>

        <button
          onClick={() =>
            setMonthDate(new Date(monthDate.setMonth(monthDate.getMonth() + 1)))
          }
          className="text-gray-600 hover:text-gray-900"
        >
          <FaChevronRight />
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 text-center font-semibold text-gray-600 mb-2">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {days.map((day, index) => {
          const dateStr = format(day, "yyyy-MM-dd");
          const holidayName = holidaysByDate[dateStr];

          return (
            <div
              key={index}
              className={`h-24 bg-white border rounded-lg p-2 flex flex-col text-xs cursor-pointer transition ${
                holidayName ? "bg-blue-50 border-blue-500" : "hover:bg-gray-50"
              }`}
              onClick={() =>
                holidayName
                  ? openModal({ name: holidayName, date: dateStr }, index)
                  : null
              }
            >
              <span
                className={`text-sm font-semibold ${
                  isToday(day)
                    ? "bg-green-500 text-white px-2 py-[1px] rounded"
                    : ""
                }`}
              >
                {format(day, "d")}
              </span>

              {holidayName && (
                <div className="mt-2 flex flex-col items-start gap-1 text-blue-700 font-semibold text-[10px] leading-tight">
                  🎉 {holidayName}

                  <button
                    className="text-blue-900 hover:text-blue-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      openModal({ name: holidayName, date: dateStr }, index);
                    }}
                  >
                    <FaRegEdit size={12} />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50 backdrop-blur-sm">
          <div className="bg-white w-full max-w-md p-6 rounded-lg shadow-lg">
            <h3 className="text-lg font-bold mb-4">
              {editIndex !== null ? "Edit Holiday" : "Add Holiday"}
            </h3>

            <label className="text-xs font-semibold">Holiday Name</label>
            <input
              value={currentHoliday.name}
              onChange={(e) =>
                setCurrentHoliday({ ...currentHoliday, name: e.target.value })
              }
              className="w-full border rounded px-2 py-2 mb-4 mt-1"
              placeholder="Enter holiday name"
            />

            <label className="text-xs font-semibold">Date</label>
            <input
              type="date"
              value={currentHoliday.date}
              onChange={(e) =>
                setCurrentHoliday({ ...currentHoliday, date: e.target.value })
              }
              className="w-full border rounded px-2 py-2 mb-4 mt-1"
            />

            <div className="flex justify-end gap-3">
              <button
                onClick={closeModal}
                className="px-4 py-2 border rounded hover:bg-gray-100"
              >
                Cancel
              </button>

              <button
                onClick={saveHoliday}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
